import type { MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import { sendGoogleAnalyticsEvent } from 'src/utils';
import type { ButtonProps } from './button.props';

import './button.scss';

const Button = ({
  analyticsEvent,
  className,
  desktopLabel,
  href = '',
  icon,
  isDisabled,
  isTabletSizeGrowth,
  label,
  onClick,
  size,
  tag = 'button',
  target,
  theme: buttonTheme,
  type = 'button',
  ...props
}: ButtonProps) => {
  const buttonClasses = classNames(
    'button',
    buttonTheme && `button_${buttonTheme}`,
    size && `button_size_${size}`,
    icon && 'button_with-icon',
    icon && size && `button_size-with-icon_${size}`,
    isDisabled && 'button_disabled',
    isTabletSizeGrowth && 'button_growth',
    className
  );

  const buttonWrapperClasses = classNames(
    'button__wrapper',
    desktopLabel && 'button__wrapper_with-desktop-label'
  );

  const renderInnerElements = () => (
    <span className={buttonWrapperClasses} data-desktop-label={desktopLabel}>
      {icon && <span className="button__icon">{icon}</span>}
      <span className="button__label">{label}</span>
    </span>
  );

  const handleClick = (event: MouseEvent) => {
    if (analyticsEvent) {
      sendGoogleAnalyticsEvent(analyticsEvent);
    }
    onClick?.(event);
  };

  switch (tag) {
    case 'button':
      return (
        <button
          aria-label={label}
          className={buttonClasses}
          onClick={handleClick}
          type={type}
          disabled={isDisabled}
          {...props}
        >
          {renderInnerElements()}
        </button>
      );
    case 'link':
      return (
        <Link
          aria-label={label}
          className={buttonClasses}
          to={href as string}
          onClick={handleClick}
          tabIndex={isDisabled ? -1 : 0}
          target={target || '_self'}
          {...props}
        >
          {renderInnerElements()}
        </Link>
      );
    case 'a':
      return (
        <a
          aria-label={label}
          className={buttonClasses}
          href={href as string}
          onClick={handleClick}
          rel="noopener noreferrer"
          tabIndex={isDisabled ? -1 : 0}
          target={target || '_blank'}
          {...props}
        >
          {renderInnerElements()}
        </a>
      );
    default:
      return null;
  }
};

export default Button;
